import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';

import ClusterApi from '../../api/ClusterApi';

// Types
import Cluster from '../../types/cluster/Cluster';
import ClusterNode from '../../types/cluster/ClusterNode';
import AvailabilityGroup from '../../types/cluster/AvailabilityGroup';
import AvailabilityReplica from '../../types/cluster/AvailabilityReplica';
import AvailabilityListener from '../../types/cluster/AvailabilityListener';
import EventsDataTable from '../../types/instance/tables/EventsDataTable';
import HostTarget from '../../types/host/HostTarget';

// Components
import ClustersTabbed from './ClustersTabbed';
import AvailabilityGroupsTable from '../../component/cluster/table/AvailabilityGroupsTable';
import ClusterNodesTable from '../../component/cluster/table/ClusterNodesTable';
import EventsTable from '../../component/instance/table/EventsTable';
import HealthBadge from '../../component/cluster/HealthBadge';

// Constants
import { DATA_LOADED, DATA_LOADING } from '../../component/Constants';

// Contexts
import { TimeRangeContext } from '../../context/TimeRangeContext';

interface IDetailProps {
    hosts: HostTarget[],
    toggleMenu: Function
}

const Detail: React.FC<IDetailProps> = (props) => {
    const { clusterId } = useParams();
    const [loading, setLoading] = useState<number>(DATA_LOADING);
    const [refreshCounter, setRefreshCounter] = useState<number>(0);
    const timeRangeContext = useContext(TimeRangeContext);

    const [cluster, setCluster] = useState<Cluster | undefined>(undefined);
    const [nodes, setNodes] = useState<ClusterNode[]>([]);
    const [groups, setGroups] = useState<AvailabilityGroup[]>([]);
    const [replicas, setReplicas] = useState<AvailabilityReplica[]>([]);
    const [listeners, setListeners] = useState<AvailabilityListener[]>([]);
    const [events, setEvents] = useState<EventsDataTable[]>([]);

    useEffect(() => {
        let active = true;
        const id = Number(clusterId);

        async function load() {
            setLoading(DATA_LOADING);
            const [clusterResult, nodesResult, groupsResult, listenersResult, eventsResult] = await Promise.all([
                ClusterApi.getCluster(id),
                ClusterApi.getNodes(id),
                ClusterApi.getAvailabilityGroups(id),
                ClusterApi.getListeners(id),
                ClusterApi.getEvents(id, timeRangeContext.timeRange),
            ]);
            if (!active) return;

            const replicasPerGroup = await Promise.all(
                groupsResult.map(group => ClusterApi.getReplicas(group.id)));
            if (!active) return;

            setCluster(clusterResult);
            setNodes(nodesResult);
            setGroups(groupsResult);
            setListeners(listenersResult);
            setReplicas(replicasPerGroup.flat());
            setEvents(eventsResult);
            setLoading(DATA_LOADED);
        }

        void load();
        return () => {
            active = false;
        };
    }, [clusterId, refreshCounter, timeRangeContext]);

    const refresh = () => {
        setLoading(DATA_LOADING);
        setRefreshCounter(counter => counter + 1);
    };

    return (
        <ClustersTabbed
            clusterId={Number(clusterId)}
            clusterName={cluster?.name ?? ''}
            toggleMenu={props.toggleMenu}
            refresh={refresh}
            loading={loading}
            activeTab="overview"
            title={`${cluster?.name ?? 'Cluster'} - DBmarlin`}
            description="Cluster overview with availability groups and nodes">

            {/* Cluster summary strip */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card">
                        <div className="card-body">
                            <span className="me-3">
                                <span className="text-muted me-1">Health:</span>
                                {cluster && <HealthBadge value={cluster.health}/>}
                            </span>
                            <span className="me-3">
                                <span className="text-muted me-1">Quorum:</span>
                                {cluster?.quorumtype} ({cluster?.quorumstate})
                            </span>
                            <span className="me-3">
                                <span className="text-muted me-1">Nodes:</span>
                                {nodes.length}
                            </span>
                            <span>
                                <span className="text-muted me-1">Availability groups:</span>
                                {groups.length}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Availability groups */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-layer-group fa-fw" aria-hidden="true"/>
                            Availability groups
                            <span className="badge bg-info">{groups.length}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseGroups" aria-expanded="false" aria-controls="collapseGroups"/>
                        </div>
                        <div id="collapseGroups" className="card-body collapse show">
                            <AvailabilityGroupsTable clusterId={Number(clusterId)} groups={groups}
                                                     replicas={replicas} listeners={listeners}/>
                        </div>
                    </div>
                </div>
            </div>

            {/* Nodes */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-server fa-fw" aria-hidden="true"/>
                            Nodes
                            <span className="badge bg-info">{nodes.length}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseNodes" aria-expanded="false" aria-controls="collapseNodes"/>
                        </div>
                        <div id="collapseNodes" className="card-body collapse show">
                            <ClusterNodesTable nodes={nodes} hosts={props.hosts}/>
                        </div>
                    </div>
                </div>
            </div>

            {/* Failovers, health changes and alerts across this cluster. Same
                data the Events tab used to show, so that tab has gone. */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-exchange fa-fw" aria-hidden="true"/>
                            Events
                            <Link className="ms-2 small" to="/admin/alert-rules">Manage rules</Link>
                            <span className="badge bg-info">{events.length}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseClusterEvents" aria-expanded="false"
                               aria-controls="collapseClusterEvents"/>
                        </div>
                        <div id="collapseClusterEvents" className="card-body collapse show">
                            <EventsTable loading={loading} eventsDataTable={events} hideRegister={true}/>
                        </div>
                    </div>
                </div>
            </div>
        </ClustersTabbed>
    );
};

export default Detail;
